"use client";
import { cn } from "@/lib/utils/utils";
import { useState, useEffect, useRef, FC, CSSProperties } from "react";

interface Position {
  x: number;
  y: number;
}

interface SmoothCursorProps {
  size?: number;
  color?: string;
  smoothFactor?: number;
  enabled?: boolean;
  className?: string;
  style?: CSSProperties;
  hideOnMobile?: boolean;
}

const SmoothCursor: FC<SmoothCursorProps> = ({
  size = 10,
  smoothFactor = 0.1,
  enabled = true,
  className = "bg-primary/50",
  style = {},
  hideOnMobile = true,
}) => {
  const [cursorPos, setCursorPos] = useState<Position>({ x: -100, y: -100 });
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const animationRef = useRef<number>(0);
  const targetPos = useRef<Position>({ x: -100, y: -100 });
  const cursorRef = useRef<HTMLDivElement>(null);
  const [isLeftClick, setIsLeftClick] = useState<boolean>(false);
  const HOLD_MS = 600;
  let timer: NodeJS.Timeout;

  // Hide cursor on mobile devices if hideOnMobile is true
  useEffect(() => {
    if (hideOnMobile) {
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        return;
      }
    }
  }, [hideOnMobile]);

  // Handle mouse move
  useEffect(() => {
    if (!enabled) return;

    const handleMouseMove = (e: MouseEvent) => {
      setIsVisible(true);
      targetPos.current = { x: e.clientX, y: e.clientY };
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseEnter = () => {
      setIsVisible(true);
    };

    window.addEventListener("mousemove", handleMouseMove);
    document.body.addEventListener("mouseleave", handleMouseLeave);
    document.body.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.body.removeEventListener("mouseleave", handleMouseLeave);
      document.body.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [enabled]);

  // Animation loop
  useEffect(() => {
    if (!enabled) return;

    const lerp = (start: number, end: number, factor: number): number => {
      return start + (end - start) * factor;
    };

    const animate = () => {
      setCursorPos((prev) => ({
        x: lerp(prev.x, targetPos.current.x, smoothFactor),
        y: lerp(prev.y, targetPos.current.y, smoothFactor),
      }));

      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [enabled, smoothFactor]);

  useEffect(() => {
    function handleLeftMouseClick(e: MouseEvent) {
      if (e.button === 0) {
        timer = setTimeout(() => {
          setIsLeftClick(true);
        }, HOLD_MS);
      }
    }

    document.body.addEventListener("mousedown", handleLeftMouseClick);
    return () => {
      document.body.removeEventListener("mousedown", handleLeftMouseClick);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  useEffect(() => {
    function handleLeftReleaseMouseClick(e: MouseEvent) {
      if (e.button === 0) {
        timer = setTimeout(() => {
          setIsLeftClick(false);
        }, HOLD_MS);
      }
    }
    document.body.addEventListener("mouseup", handleLeftReleaseMouseClick);
    return () => {
      document.body.removeEventListener("mouseup", handleLeftReleaseMouseClick);
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, []);

  // Combine custom styles with defaults
  const cursorStyle: CSSProperties = {
    position: "fixed",
    left: cursorPos.x,
    top: cursorPos.y,
    width: `${size}px`,
    height: `${size}px`,
    borderRadius: "50%",
    pointerEvents: "none",
    transform: "translate(-50%, -50%)",
    zIndex: 9999,
    opacity: isVisible ? 1 : 0,
    transition:
      "opacity 0.2s ease, transform 0.1s ease-out, scale 0.1s ease-out",
    willChange: "transform",
    ...style,
  };

  if (!enabled) return null;

  return (
    <div
      ref={cursorRef}
      className={cn("smooth-cursor", className, isLeftClick && "scale-125")}
      style={cursorStyle}
      aria-hidden="true"
    />
  );
};

export default SmoothCursor;
